
import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag, CheckSquare, Square, Loader2 } from 'lucide-react';
import { useSupabaseData } from '@/hooks/useSupabaseData';
import { useUpdateTag, useDeleteTag, useDeleteMultipleTags } from '@/hooks/useTagsData';
import { useClinicaOperations } from '@/hooks/useClinicaOperations';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CabecalhoConfiguracoes } from '@/components/settings/layout/CabecalhoConfiguracoes';
import {
  CartaoConfiguracoes as Card,
  CabecalhoCartaoConfiguracoes as CardHeader,
  TituloCartaoConfiguracoes as CardTitle,
  ConteudoCartaoConfiguracoes as CardContent,
} from '@/components/settings/layout/CartaoConfiguracoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Componente para gerenciar tags/categorias de leads - 100% interno
 * 
 * Funcionalidades:
 * - Criar novas tags com cores personalizadas
 * - Editar tags existentes
 * - Excluir tag individual com modal de confirmação
 * - Seleção múltipla de tags com checkboxes
 * - Exclusão em lote de múltiplas tags
 * - Visualizar tags em uma grade organizada
 * - Integração completa com Supabase e RLS
 * 
 * Onde é usado:
 * - SettingsPage.tsx (aba "Tags")
 * 
 * Como se conecta:
 * - Tabela tags: busca/cria/atualiza/deleta tags
 * - Políticas RLS: garante isolamento por clínica
 * - useTagsData: hooks de CRUD
 * - AlertDialog: modal de confirmação profissional
 * - Checkbox: seleção múltipla
 */

export const TagManager = () => {
  const { tags, loading } = useSupabaseData();
  const { createTag } = useClinicaOperations();
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();
  const deleteMultipleTagsMutation = useDeleteMultipleTags();

  const [isCreating, setIsCreating] = useState(false);
  const [editingTag, setEditingTag] = useState<any>(null);
  const [formData, setFormData] = useState({
    nome: '',
    cor: '#3B82F6'
  });

  // Estados para seleção múltipla e exclusão
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Cores predefinidas para seleção
  const coresPredefinidas = [
    '#3B82F6', // Azul
    '#10B981', // Verde
    '#F59E0B', // Amarelo
    '#EF4444', // Vermelho
    '#8B5CF6', // Roxo
    '#F97316', // Laranja
    '#06B6D4', // Ciano
    '#84CC16', // Lima
    '#EC4899', // Rosa
    '#6B7280'  // Cinza
  ];

  // Função para iniciar criação de nova tag
  const handleNovaTag = () => {
    setFormData({ nome: '', cor: '#3B82F6' });
    setEditingTag(null);
    setIsCreating(true);
  };

  // Função para iniciar edição de tag
  const handleEditarTag = (tag: any) => {
    setFormData({ nome: tag.nome, cor: tag.cor });
    setEditingTag(tag);
    setIsCreating(true);
  };

  // Função para salvar tag (criar ou editar)
  const handleSalvarTag = async () => {
    try {
      if (!formData.nome.trim()) {
        // Validação: tag sem nome não aparece no chat nem no Kanban; avisamos com o toast do sistema (antes era alert do navegador).
        toast.error('Dê um nome para a tag.');
        return;
      }

      if (editingTag) {
        // Editando tag existente
        await updateTagMutation.mutateAsync({
          id: editingTag.id,
          nome: formData.nome,
          cor: formData.cor
        });
      } else {
        // Criando nova tag - usar hook de operações da clínica
        await createTag({
          nome: formData.nome,
          cor: formData.cor
        });
      }

      // Limpar formulário e fechar modal
      setFormData({ nome: '', cor: '#3B82F6' });
      setIsCreating(false);
      setEditingTag(null);
    } catch (error) {
      console.error('Erro ao salvar tag:', error);
      toast.error('Não foi possível salvar a tag. Tente de novo.');
    }
  };

  // Alternar seleção de uma tag específica
  const toggleTagSelection = (tagId: string) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  // Selecionar todas as tags visíveis
  const handleSelectAll = () => {
    if (selectedTags.length === tags.length) {
      setSelectedTags([]);
    } else {
      setSelectedTags(tags.map((tag: any) => tag.id));
    }
  };

  // Ativar/desativar modo de seleção
  const toggleSelectionMode = () => {
    setIsSelectionMode(prev => !prev);
    if (isSelectionMode) {
      setSelectedTags([]);
    }
  };

  // Função para iniciar exclusão de tag individual
  const handleExcluirTag = (tag: any) => {
    setTagToDelete(tag);
    setDeleteConfirmOpen(true);
  };

  // Função que executa a exclusão após confirmação
  const executarExclusaoIndividual = async () => {
    if (!tagToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTagMutation.mutateAsync(tagToDelete.id);
      setDeleteConfirmOpen(false);
      setTagToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir tag:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Função para excluir múltiplas tags selecionadas
  const handleExcluirSelecionadas = async () => {
    if (selectedTags.length === 0) return;

    setIsDeleting(true);
    try {
      await deleteMultipleTagsMutation.mutateAsync(selectedTags);
      setSelectedTags([]);
      setIsSelectionMode(false);
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error('Erro ao excluir tags em lote:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Função para iniciar exclusão em lote
  const iniciarExclusaoEmLote = () => {
    setTagToDelete(null);
    setDeleteConfirmOpen(true);
  };

  // Função para cancelar criação/edição
  const handleCancelar = () => {
    setFormData({ nome: '', cor: '#3B82F6' });
    setIsCreating(false);
    setEditingTag(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Carregando tags...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      <CabecalhoConfiguracoes
        titulo="Tags"
        descricao="Organize seus leads com tags personalizadas."
        icone={Tag}
        acoes={
          <>
              {tags.length > 0 && (
                <Button
                  variant={isSelectionMode ? 'default' : 'outline'}
                  onClick={toggleSelectionMode}
                  className="min-h-11 gap-2"
                >
                  {isSelectionMode ? <CheckSquare size={18} /> : <Square size={18} />}
                  {isSelectionMode ? 'Cancelar seleção' : 'Selecionar várias'}
                </Button>
              )}
              {isSelectionMode && selectedTags.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={iniciarExclusaoEmLote}
                  disabled={isDeleting}
                  className="min-h-11 gap-2"
                >
                  <Trash2 size={18} />
                  Excluir ({selectedTags.length})
                </Button>
              )}
              <Button
                onClick={handleNovaTag}
                disabled={isSelectionMode}
                className="min-h-11 gap-2"
              >
                <Plus size={18} />
                Nova tag
              </Button>
          </>
        }
      />

      {/* Formulário de criação/edição */}
      {isCreating && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingTag ? 'Editar tag' : 'Nova tag'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="tag-name">Nome da tag</Label>
                <Input
                  id="tag-name"
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Digite o nome da tag"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Cor da tag</Label>
                <div className="flex gap-2 flex-wrap">
                  {coresPredefinidas.map((cor) => (
                    <button
                      key={cor}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, cor }))}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.cor === cor 
                          ? 'border-foreground scale-110' 
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: cor }}
                      title={cor}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="color"
                    value={formData.cor}
                    onChange={(e) => setFormData(prev => ({ ...prev, cor: e.target.value }))}
                    className="w-10 h-10 border border-input rounded-lg cursor-pointer bg-background"
                    aria-label="Selecionar cor personalizada"
                  />
                  <span className="text-xs text-muted-foreground font-mono">{formData.cor}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleCancelar}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSalvarTag}
                disabled={!formData.nome.trim()}
              >
                {editingTag ? 'Salvar alterações' : 'Criar tag'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de tags */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Suas tags</CardTitle>
            
            {/* Checkbox para selecionar todas (visível apenas no modo de seleção) */}
            {isSelectionMode && tags.length > 0 && (
              <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                <Checkbox
                  checked={selectedTags.length === tags.length && tags.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-muted-foreground">
                  Selecionar todas ({selectedTags.length}/{tags.length})
                </span>
              </label>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <div className="text-center py-10">
              <Tag size={48} className="text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-muted-foreground">Nenhuma tag criada ainda</p>
              <p className="text-muted-foreground/70 text-sm">Crie sua primeira tag para organizar os leads</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {tags.map((tag: any) => (
                <div
                  key={tag.id}
                  className={`flex items-center justify-between p-3 border rounded-xl transition-all ${
                    selectedTags.includes(tag.id)
                      ? 'border-ring bg-accent'
                      : 'border-border bg-card'
                  } ${
                    isSelectionMode ? 'cursor-pointer hover:border-input' : ''
                  }`}
                  onClick={() => isSelectionMode && toggleTagSelection(tag.id)}
                >
                  <div className="flex items-center gap-3">
                    {/* Checkbox (visível apenas no modo de seleção) */}
                    {isSelectionMode && (
                      <Checkbox
                        checked={selectedTags.includes(tag.id)}
                        onCheckedChange={() => toggleTagSelection(tag.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0 border border-black/10"
                      style={{ backgroundColor: tag.cor }}
                    />
                    <span className="font-medium text-sm">{tag.nome}</span>
                  </div>
                  
                  {/* Botões de ação (ocultos no modo de seleção) */}
                  {!isSelectionMode && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-accent-foreground hover:text-accent-foreground hover:bg-accent"
                        onClick={() => handleEditarTag(tag)}
                        title="Editar tag"
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleExcluirTag(tag)}
                        title="Excluir tag"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estatísticas */}
      <Card>
        <CardHeader>
          <CardTitle>Estatísticas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-2xl bg-muted">
              <div className="font-display text-2xl font-bold tabular-nums text-foreground">{tags.length}</div>
              <div className="text-sm text-muted-foreground">Tags criadas</div>
            </div>
            <div className="text-center p-4 rounded-2xl bg-muted">
              <div className="font-display text-2xl font-bold tabular-nums text-foreground">
                {coresPredefinidas.filter(cor => tags.some((tag: any) => tag.cor === cor)).length}
              </div>
              <div className="text-sm text-muted-foreground">Cores da paleta em uso</div>
            </div>
            <div className="text-center p-4 rounded-2xl bg-muted">
              <div className="font-display text-2xl font-bold tabular-nums text-foreground">
                {tags.filter((tag: any) => !coresPredefinidas.includes(tag.cor)).length}
              </div>
              <div className="text-sm text-muted-foreground">Tags com cor própria</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Confirmação de Exclusão */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tagToDelete 
                ? `Confirmar exclusão da tag "${tagToDelete.nome}"` 
                : `Confirmar exclusão de ${selectedTags.length} tag(s)`
              }
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tagToDelete ? (
                <>
                  Tem certeza que deseja excluir a tag <strong>"{tagToDelete.nome}"</strong>?
                  <span className="text-destructive font-medium mt-2 block">
                    Esta ação não pode ser desfeita. A tag será removida de todos os leads.
                  </span>
                </>
              ) : (
                <>
                  Tem certeza que deseja excluir <strong>{selectedTags.length} tags</strong> selecionadas?
                  <span className="text-destructive font-medium mt-2 block">
                    Esta ação não pode ser desfeita. As tags serão removidas de todos os leads.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={tagToDelete ? executarExclusaoIndividual : handleExcluirSelecionadas}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="mr-2" />
                  Excluir {tagToDelete ? 'Tag' : `${selectedTags.length} Tags`}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
